/**
 * W5 plan curve — the prominent depth-vs-days curve derived from the ordered
 * phases. Dependency-free SVG built on the shared ChartFrame (no Plotly, no
 * external refs — CSP-safe). Y grows downward (a depth chart); X is elapsed
 * days (accumulated hours ÷ 24). Each phase boundary is a vertex, and the phase
 * markers/labels are driven entirely by the derived curve so an edit or reorder
 * upstream reshapes it live.
 */
import { useMemo } from "react";
import { ChartFrame } from "@components/chart/ChartFrame";
import type { CurvePoint } from "./programModel";

export interface PlanCurveChartProps {
  points: CurvePoint[];
  title?: string;
  xLabel?: string;
  yLabel?: string;
  ariaLabel?: string;
  /** The stroke token for the curve (defaults to the unconventional op color). */
  seriesVar?: string;
}

/** Round a domain max up to a "nice" step so the axis labels read cleanly. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / mag;
  const step = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return step * mag;
}

export function PlanCurveChart({
  points,
  title,
  xLabel,
  yLabel,
  ariaLabel,
  seriesVar = "--op-unconv",
}: PlanCurveChartProps) {
  const xMax = useMemo(() => niceMax(Math.max(1, ...points.map((p) => p.days))), [points]);
  const yMax = useMemo(() => niceMax(Math.max(1, ...points.map((p) => p.depth))), [points]);

  if (points.length === 0) return null;

  const stroke = `var(${seriesVar})`;

  return (
    <ChartFrame
      title={title}
      xDomain={[0, xMax]}
      yDomain={[0, yMax]}
      yDown
      width={900}
      height={340}
      xLabel={xLabel}
      yLabel={yLabel}
      ariaLabel={ariaLabel}
    >
      {({ plot, sx, sy }) => {
        const xTicks = axisTicks(xMax, 5);
        const yTicks = axisTicks(yMax, 4);
        const line = points.map((p) => `${sx(p.days)},${sy(p.depth)}`).join(" ");
        return (
          <g>
            {/* horizontal gridlines + y (depth) tick labels */}
            {yTicks.map((v) => (
              <g key={`y${v}`}>
                <line
                  x1={plot.x}
                  x2={plot.x + plot.w}
                  y1={sy(v)}
                  y2={sy(v)}
                  stroke="var(--line)"
                  strokeWidth={0.5}
                  opacity={0.55}
                />
                <text
                  x={plot.x - 6}
                  y={sy(v) + 3}
                  textAnchor="end"
                  fontSize={9.5}
                  fontFamily="var(--font-mono)"
                  fill="var(--dim)"
                >
                  {Math.round(v).toLocaleString()}
                </text>
              </g>
            ))}
            {/* vertical day gridlines + x tick labels */}
            {xTicks.map((v) => (
              <g key={`x${v}`}>
                <line
                  x1={sx(v)}
                  x2={sx(v)}
                  y1={plot.y}
                  y2={plot.y + plot.h}
                  stroke="var(--line)"
                  strokeWidth={0.5}
                  opacity={0.35}
                  strokeDasharray="3,3"
                />
                <text
                  x={sx(v)}
                  y={plot.y + plot.h + 14}
                  textAnchor="middle"
                  fontSize={9}
                  fontFamily="var(--font-mono)"
                  fill="var(--dim)"
                >
                  D{v % 1 === 0 ? v : v.toFixed(1)}
                </text>
              </g>
            ))}
            {/* the plan curve */}
            <polyline points={line} fill="none" stroke={stroke} strokeWidth={3} />
            {/* phase-boundary markers */}
            {points.map((p) => (
              <circle
                key={`m${p.seq}`}
                cx={sx(p.days)}
                cy={sy(p.depth)}
                r={5}
                fill="var(--surface)"
                stroke={stroke}
                strokeWidth={2.5}
              >
                <title>
                  {p.phase} — D{p.days.toFixed(1)} · {Math.round(p.depth).toLocaleString()} m
                </title>
              </circle>
            ))}
          </g>
        );
      }}
    </ChartFrame>
  );
}

/** Evenly-spaced axis ticks 0..max (inclusive), `count` intervals. */
function axisTicks(max: number, count: number): number[] {
  const step = max / count;
  return Array.from({ length: count + 1 }, (_, i) => Math.round(i * step * 100) / 100);
}
